import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import TreasuryWidget from "@/components/TreasuryWidget";
import {
  CalendarDays,
  MapPin,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Clock,
  Users,
  Cake,
} from "lucide-react";
import { toast } from "sonner";

const statusMap = {
  available: { label: "Présent", color: "emerald", Icon: CheckCircle2 },
  unavailable: { label: "Absent", color: "rose", Icon: XCircle },
  pending: { label: "Incertain", color: "amber", Icon: Clock },
};

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Dashboard() {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [nextMatchFull, setNextMatchFull] = useState(null);
  const [nextBirthday, setNextBirthday] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const [m, lb, bd, ps] = await Promise.all([
        api.get("/matches"),
        api.get("/leaderboard"),
        api.get("/birthdays/upcoming?limit=1").catch(() => ({ data: [] })),
        api.get("/payments/my-status").catch(() => ({ data: null })),
      ]);
      setMatches(m.data);
      setNextBirthday((bd.data && bd.data[0]) || null);
      setPaymentStatus(ps.data);
      const mine = lb.data.find((r) => r.player_id === user?.id);
      setMyStats(
        mine || {
          goals: 0,
          assists: 0,
          yellow_cards: 0,
          red_cards: 0,
          matches_played: 0,
        }
      );

      const upcoming = m.data
        .filter(
          (mm) => new Date(mm.match_date) >= new Date(Date.now() - 3600000) && mm.status !== "finished"
        )
        .sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
      if (upcoming[0]) {
        const { data } = await api.get(`/matches/${upcoming[0].id}`);
        setNextMatchFull(data);
      } else {
        setNextMatchFull(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [user?.id]);

  const setAvailability = async (matchId, status) => {
    try {
      await api.post(`/matches/${matchId}/availability`, { status });
      toast.success(`Disponibilité : ${statusMap[status].label}`);
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const myAvail = nextMatchFull?.availabilities?.find((a) => a.player_id === user?.id)?.status;
  const presentCount = (nextMatchFull?.availabilities || []).filter((a) => a.status === "available").length;

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-slate-500 mb-2">
          Salut {user?.name?.split(" ")[0]}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight">
            Tableau de bord
          </h1>
          {paymentStatus && (
            <Link
              to="/paiement"
              data-testid="cotisation-badge"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono-stat uppercase tracking-widest border ${
                paymentStatus.has_paid
                  ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                  : "bg-amber-500/15 text-amber-300 border-amber-500/40 hover:bg-amber-500/25"
              }`}
              title={paymentStatus.has_paid ? "Contribution à jour" : "Aucun paiement — cliquer pour contribuer"}
            >
              {paymentStatus.has_paid ? (
                <>
                  <CheckCircle2 className="w-3 h-3" /> Contribution à jour
                </>
              ) : (
                <>
                  <Clock className="w-3 h-3" /> Aucune contribution
                </>
              )}
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Buts marqués" value={myStats?.goals ?? 0} accent="#E5B85C" testid="stat-goals" />
        <StatCard label="Assists" value={myStats?.assists ?? 0} accent="#60A5FA" testid="stat-assists" />
        <StatCard label="Cartons jaunes" value={myStats?.yellow_cards ?? 0} accent="#EAB308" testid="stat-yellow" />
        <StatCard label="Matchs joués" value={myStats?.matches_played ?? 0} accent="#DC2626" testid="stat-matches" />
      </div>

      {(user?.role === "coach" || user?.role === "admin") && <TreasuryWidget />}

      {nextMatchFull ? (
        <div
          data-testid="next-match-card"
          className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900 to-[#0B0F17] p-6 sm:p-8"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#E5B85C]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E5B85C]/10 border border-[#E5B85C]/30 mb-4">
              <span className="text-[10px] font-mono-stat uppercase tracking-widest text-[#E5B85C]">
                Prochain match
              </span>
            </div>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
                  {nextMatchFull.title || "Match ASS les Régionnaires"}
                </h2>
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-300">
                  <span className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" /> {formatDate(nextMatchFull.match_date)}
                  </span>
                  <span className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> {nextMatchFull.location}
                  </span>
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4" /> {presentCount} présent(s)
                  </span>
                </div>
              </div>
              <Link
                to={`/matches/${nextMatchFull.id}`}
                data-testid="next-match-open"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg"
              >
                Ouvrir <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {user?.role === "player" && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="text-[11px] font-mono-stat uppercase tracking-widest text-slate-400 mb-3">
                  Ma disponibilité
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(statusMap).map(([k, v]) => (
                    <button
                      key={k}
                      data-testid={`avail-${k}`}
                      onClick={() => setAvailability(nextMatchFull.id, k)}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-bold uppercase tracking-wide transition-colors ${
                        myAvail === k
                          ? `bg-${v.color}-500/30 border-${v.color}-500 text-${v.color}-200`
                          : `bg-${v.color}-500/10 border-${v.color}-500/30 text-${v.color}-300 hover:bg-${v.color}-500/20`
                      }`}
                    >
                      <v.Icon className="w-4 h-4" />
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        !loading && (
          <div className="p-8 rounded-2xl border border-dashed border-white/10 bg-slate-900/50 text-center">
            <div className="font-display font-bold text-2xl uppercase text-slate-500">
              Aucun match programmé
            </div>
            <div className="text-sm text-slate-400 mt-2">
              {user?.role === "coach" || user?.role === "admin"
                ? "Créez le prochain match depuis la section Matchs."
                : "Attendez que le coach programme le prochain match."}
            </div>
          </div>
        )
      )}

      {nextBirthday && (
        <BirthdayWidget birthday={nextBirthday} />
      )}

      {matches.filter((m) => m.status !== "finished").length > 1 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-2xl uppercase tracking-tight">À venir</h3>
            <Link to="/matches" className="text-xs uppercase text-[#E5B85C] font-semibold">
              Voir tout →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {matches
              .filter((m) => m.status !== "finished")
              .slice(1, 5)
              .map((m) => (
                <Link
                  key={m.id}
                  to={`/matches/${m.id}`}
                  data-testid={`upcoming-match-${m.id}`}
                  className="p-4 rounded-xl bg-slate-900 border border-white/10 hover:border-[#E5B85C]/50 transition-colors block"
                >
                  <div className="font-bold text-sm">{m.title || "Match"}</div>
                  <div className="text-xs text-slate-400 mt-1 font-mono-stat">
                    {formatDate(m.match_date)} · {m.location}
                  </div>
                </Link>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="p-5 rounded-xl bg-slate-900 border border-white/10 relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 h-1 w-full" style={{ background: accent }} />
      <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400">
        {label}
      </div>
      <div className="font-display font-black text-4xl mt-2" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function BirthdayWidget({ birthday }) {
  const d = birthday.days_until;
  let badgeText;
  let badgeClass;
  if (d === 0) {
    badgeText = "AUJOURD'HUI 🎂";
    badgeClass = "bg-[#E5B85C] text-[#0B0F17]";
  } else if (d === 1) {
    badgeText = "DEMAIN";
    badgeClass = "bg-[#E5B85C]/25 text-[#E5B85C] border border-[#E5B85C]/40";
  } else if (d <= 7) {
    badgeText = `J-${d}`;
    badgeClass = "bg-orange-500/20 text-orange-300 border border-orange-500/40";
  } else {
    badgeText = `dans ${d}j`;
    badgeClass = "bg-slate-800 text-slate-300 border border-white/10";
  }
  const dateLabel = (() => {
    // next_birthday is a real ISO date computed by backend; format day + month only
    try {
      return new Date(birthday.next_birthday).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
      });
    } catch {
      return "";
    }
  })();
  return (
    <div
      data-testid="birthday-widget"
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#2A1D06] to-[#0B0F17] p-5 sm:p-6"
    >
      <div className="absolute top-0 right-0 w-40 h-40 bg-[#E5B85C]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="shrink-0 w-14 h-14 rounded-xl bg-[#E5B85C]/15 border border-[#E5B85C]/40 flex items-center justify-center">
          <Cake className="w-7 h-7 text-[#E5B85C]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono-stat uppercase tracking-[0.25em] text-[#E5B85C] mb-1">
            Prochain anniversaire
          </div>
          <div className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight truncate">
            {birthday.name}
          </div>
          <div className="text-xs sm:text-sm text-slate-400 mt-1 font-mono-stat">
            {dateLabel}
          </div>
        </div>
        <span
          data-testid="birthday-countdown-badge"
          className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-mono-stat font-bold uppercase tracking-widest whitespace-nowrap ${badgeClass}`}
        >
          {badgeText}
        </span>
      </div>
    </div>
  );
}
